import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent as track } from '../../utils/analytics';
import { heroCopy, inputs, channels } from '../../data/communicationsHero';
import { AudienceMap } from './hero/AudienceMap';
import { CommsPhone } from './hero/CommsPhone';
import { AlertCard, InputCard, ChannelCard, TargetedCard, Callout, ResultsStrip } from './hero/HeroCards';
const EASE = [0.23, 1, 0.32, 1] as const;

// Connector geometry for the desktop orchestration row (viewBox 1000 × 340).
const IN_Y = [42, 127, 212, 297];
const OUT_Y = [28, 85, 142, 198, 255, 312];
export function CommunicationsHero() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: {
    fr: string;
    en: string;
  }) => isFR ? v.fr : v.en;
  const lines = isFR ? heroCopy.lines.fr : heroCopy.lines.en;
  return <section className="relative overflow-hidden pt-14 lg:pt-16 pb-16 lg:pb-20 px-6 bg-[#FCFCFE]" data-id="element-2392" data-mp="3v6kc5upsegzv">
      <div className="pointer-events-none absolute -left-40 top-20 w-[520px] h-[520px] rounded-full bg-[#EEEDFC] opacity-70 blur-[120px]" aria-hidden="true" data-id="element-2393" data-mp="3r5i9f3wnvmt5" />
      <div className="pointer-events-none absolute right-0 top-1/3 w-[620px] h-[480px] rounded-full bg-[#EFEEFD] opacity-70 blur-[130px]" aria-hidden="true" data-id="element-2394" data-mp="8sk9uy3s6vzi" />

      <div className="relative max-w-[1680px] mx-auto grid grid-cols-1 min-[1360px]:grid-cols-[minmax(0,0.62fr)_minmax(0,1.38fr)] gap-12 min-[1360px]:gap-10 items-center" data-id="element-2395" data-mp="ionh2idhpktb">
        {/* LEFT — message */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-2396" data-mp="rp28lzh1rw59">
          <p className="font-sans text-[12.5px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-2397" data-mp="mrtlrlkyf3xo">{heroCopy.eyebrow}</p>
          <h1 className="font-serif text-[3rem] sm:text-[4rem] xl:text-[4.8rem] min-[1360px]:text-[3.8rem] 2xl:text-[4.6rem] min-[1680px]:text-[5rem] text-[#121212] leading-[1] tracking-tight mt-7" data-id="element-2398" data-mp="2oietwwhl981v">
            {lines.map(l => <span key={l} className="block" data-id="element-2399" data-mp="xdk1k2jbqciq">
                {l}
              </span>)}
            <span className="block text-[#4F46E5]" data-id="element-2400" data-mp="12d6leutnqr0i">{pick(heroCopy.accent)}</span>
          </h1>
          <p className="font-sans text-[17px] md:text-[18px] text-[#5B6478] leading-[1.65] mt-7 max-w-[520px]" data-id="element-2401" data-mp="16fa15vnd0hrl">{pick(heroCopy.body)}</p>

          <div className="flex flex-wrap items-center gap-4 mt-9" data-id="element-2402" data-mp="1bdurn3ct7n2u">
            <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
            location: 'Communications Hero'
          })} className="inline-flex items-center gap-3 px-8 py-4 rounded-lg bg-[#4F46E5] text-white font-sans text-[15px] font-medium shadow-[0_14px_30px_-16px_rgba(79,70,229,0.8)] transition-colors duration-150 hover:bg-[#4338CA]" data-id="element-2403" data-mp="1gc61vf70nzll">
              {pick(heroCopy.ctaPrimary)}
              <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} aria-hidden="true" data-id="element-2404" data-mp="1z3yu37e9hgs9" />
            </Link>
            <a href="#contact" onClick={e => {
            e.preventDefault();
            document.getElementById('contact')?.scrollIntoView({
              behavior: 'smooth'
            });
            track('Emergency CTA Clicked', {
              location: 'Communications Hero'
            });
          }} className="inline-flex items-center gap-3 px-8 py-4 rounded-lg border border-[#E0E2EA] bg-white text-[#121212] font-sans text-[15px] font-medium transition-colors duration-150 hover:border-[#4F46E5] hover:text-[#4F46E5]" data-id="element-2405" data-mp="3jjn794sikzdu">
              {pick(heroCopy.ctaSecondary)}
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2406" data-mp="3tht59bb79ldf" />
            </a>
          </div>
          <p className="font-sans text-[11.5px] font-medium tracking-[0.28em] uppercase text-[#A3A8B6] mt-9" data-id="element-2407" data-mp="3oiqbwo4l13la">{pick(heroCopy.values)}</p>
        </motion.div>

        {/* RIGHT — orchestration */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.08,
        ease: EASE
      }} className="relative min-w-0" data-id="element-2408" data-mp="l2130vhj1hsg">
          {/* DESKTOP composition */}
          <div className="hidden lg:block" data-id="element-2409" data-mp="3rucge64iar2s">
            <div className="grid grid-cols-[minmax(0,1fr)_210px] xl:grid-cols-[minmax(0,1fr)_220px] gap-x-6" data-id="element-2410" data-mp="3rvdukjgtjtoq">
              {/* main column */}
              <div className="min-w-0" data-id="element-2411" data-mp="occ3cck3fvib">
                <div className="ml-[16%] mr-[12%]" data-id="element-2412" data-mp="oddqvgiaqagx">
                  <AlertCard isFR={isFR} data-id="element-2413" data-mp="sepc79u509jb" />
                </div>

                <div className="relative mt-5 h-[360px]" data-id="element-2414" data-mp="sg0awv8q6r3k">
                  <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1000 340" preserveAspectRatio="none" aria-hidden="true" data-id="element-2415" data-mp="elznuyr4ysj1">
                    {IN_Y.map(y => <path key={`in${y}`} d={`M272 ${y} C 380 ${y}, 380 170, 470 170`} fill="none" stroke="#7C70F2" strokeWidth="1.2" vectorEffect="non-scaling-stroke" data-id="element-2416" data-mp="17aoeu4yh9hh2" />)}
                    {OUT_Y.map(y => <path key={`out${y}`} d={`M600 170 C 700 170, 700 ${y}, 808 ${y}`} fill="none" stroke="#7C70F2" strokeWidth="1.2" vectorEffect="non-scaling-stroke" data-id="element-2417" data-mp="1v5gor2s3r7en" />)}
                  </svg>

                  {/* inputs */}
                  <div className="absolute left-0 top-0 w-[27%] h-full flex flex-col justify-between" data-id="element-2418" data-mp="1chlfnw7fqhrv">
                    {inputs.map(it => <InputCard key={it.key} isFR={isFR} item={it} data-id="element-2419" data-mp="2nuxnei6bfkjb" />)}
                  </div>
                  {/* map */}
                  <div className="absolute left-[29%] w-[50%] top-0 h-full" data-id="element-2420" data-mp="2ivukp4dt5qei">
                    <AudienceMap className="w-full h-full" data-id="element-2421" data-mp="2qw0jtod6jds5" />
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-2422" data-mp="2uyngc2kspmvo">
                      <TargetedCard isFR={isFR} data-id="element-2423" data-mp="2zxqhhbxnn8yd" />
                    </div>
                  </div>
                  {/* channels */}
                  <div className="absolute right-0 top-0 w-[19%] h-full flex flex-col justify-between" data-id="element-2424" data-mp="34vs1bz2wncaw">
                    {channels.map(c => <ChannelCard key={c.key} isFR={isFR} item={c} data-id="element-2425" data-mp="27788jua92dd1" />)}
                  </div>
                </div>
              </div>

              {/* phone column */}
              <div className="relative flex flex-col items-center" data-id="element-2426" data-mp="2h55uuc8ju030">
                <Callout isFR={isFR} data-id="element-2427" data-mp="2lyqus9tpd44u" />
                <svg className="w-24 h-14 -my-1" viewBox="0 0 96 56" aria-hidden="true" data-id="element-2428" data-mp="2lzs9qpduaaru">
                  <path d="M48 2 C 50 26, 30 30, 36 54" fill="none" stroke="#8B7FF7" strokeWidth="1.2" strokeDasharray="3 4" data-id="element-2429" data-mp="1ogh1izdqiz1t" />
                  <circle cx="48" cy="3" r="2.5" fill="#6D5DF6" data-id="element-2430" data-mp="1lxvg1iy5v12r" />
                </svg>
                <CommsPhone isFR={isFR} data-id="element-2431" data-mp="1gz2e8ns8kyif" />
                {/* dotted link back to alert card */}
                <svg className="absolute -left-[150px] top-[56px] w-[150px] h-[80px]" viewBox="0 0 150 80" aria-hidden="true" data-id="element-2432" data-mp="1bzz7mywlyk8c">
                  <path d="M0 10 C 60 6, 110 30, 146 76" fill="none" stroke="#8B7FF7" strokeWidth="1.2" strokeDasharray="3 4" data-id="element-2433" data-mp="171ny6pa86bqn" />
                  <path d="M4 6 L0 10 L4 14" fill="none" stroke="#8B7FF7" strokeWidth="1.2" data-id="element-2434" data-mp="1302n99h947j3" />
                </svg>
              </div>
            </div>

            <div className="mt-6" data-id="element-2435" data-mp="t98268fqrdz8">
              <ResultsStrip isFR={isFR} data-id="element-2436" data-mp="taj2tzdw45m4" />
            </div>
          </div>

          {/* MOBILE / TABLET stack */}
          <div className="lg:hidden space-y-5" data-id="element-2437" data-mp="oby6wc5o883h">
            <AlertCard isFR={isFR} data-id="element-2438" data-mp="jb211t1rebvf" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2439" data-mp="ja0f2tj7g4yx">
              {inputs.map(it => <InputCard key={it.key} isFR={isFR} item={it} data-id="element-2440" data-mp="fa8ep43yt54n" />)}
            </div>
            <ArrowDownIcon className="w-5 h-5 text-[#4F46E5] mx-auto" strokeWidth={1.5} aria-hidden="true" data-id="element-2441" data-mp="237plrl25mc18" />
            <div className="relative mx-auto max-w-[460px]" data-id="element-2442" data-mp="284zdj7d0mzs8">
              <AudienceMap className="w-full h-auto" data-id="element-2443" data-mp="1t9067yxqd3o5" />
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-2444" data-mp="1tabby4aqklhw">
                <TargetedCard isFR={isFR} data-id="element-2445" data-mp="1y9eg7miw6not" />
              </div>
            </div>
            <ArrowDownIcon className="w-5 h-5 text-[#4F46E5] mx-auto" strokeWidth={1.5} aria-hidden="true" data-id="element-2446" data-mp="1kec24c3w5s0x" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-id="element-2447" data-mp="1p7y3h84lu9hf">
              {channels.map(c => <ChannelCard key={c.key} isFR={isFR} item={c} data-id="element-2448" data-mp="1afmioewcjzik" />)}
            </div>
            <div className="flex flex-col items-center gap-4 pt-2" data-id="element-2449" data-mp="1fepoi1k5i9rl">
              <Callout isFR={isFR} data-id="element-2450" data-mp="11ic6mrbpg6cl" />
              <CommsPhone isFR={isFR} data-id="element-2451" data-mp="11jevktpgvypv" />
            </div>
            <ResultsStrip isFR={isFR} data-id="element-2452" data-mp="15hl1y0me8ca9" />
          </div>
        </motion.div>
      </div>
    </section>;
}